import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UB_DISTRICTS, khorooOptions } from '../data/mongoliaDistricts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Файл байршуулах г.м. алдаа гарвал харуулах техникийн туслалцааны мессеж.
const SUPPORT_LINE = '99101483 дугаар руу техникийн туслалцаа авна уу.';

interface BoardMember {
  name: string;
  email: string;
  phone: string;
  register: string;
  workplace: string;
  role: string;
}

const emptyMember = (): BoardMember => ({ name: '', email: '', phone: '', register: '', workplace: '', role: 'member' });

export default function HoaRegistrationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [addingDocs, setAddingDocs] = useState(false);
  const [docMsg, setDocMsg] = useState('');
  const [reupload, setReupload] = useState(false);

  // Дүүргээс нэмэлт баримт шаардсан имэйлийн линк (?request=<id>) — тухайн
  // өргөдөлд шууд файл нэмэх дэлгэц рүү оруулна.
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get('request');
    if (rid) { setRequestId(rid); setReupload(true); setSuccess(true); }
  }, []);

  const [org_name, setOrgName] = useState('');
  const [registration_number, setRegNumber] = useState('');
  const [address, setAddress] = useState('');
  const [contact_phone, setPhone] = useState('');
  const [contact_email, setEmail] = useState('');
  const [district, setDistrict] = useState('');
  const [khoroo, setKhoroo] = useState('');

  const [members, setMembers] = useState<BoardMember[]>([
    { ...emptyMember(), role: 'director' },
    emptyMember(),
    emptyMember(),
  ]);

  const meetingRef = useRef<HTMLInputElement>(null);
  const resolutionRef = useRef<HTMLInputElement>(null);
  const signaturesRef = useRef<HTMLInputElement>(null);
  const propertyRef = useRef<HTMLInputElement>(null);

  const updateMember = (idx: number, field: keyof BoardMember, val: string) => {
    const updated = [...members];
    updated[idx] = { ...updated[idx], [field]: val };
    setMembers(updated);
  };

  const addMember = () => setMembers([...members, emptyMember()]);
  const removeMember = (idx: number) => {
    if (members.length <= 3) return;
    setMembers(members.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org_name || !contact_phone || !contact_email) {
      setError('Шаардлагатай талбаруудыг бөглөнө үү'); return;
    }
    const validMembers = members.filter(m => m.name && m.phone);
    if (validMembers.length < 3) {
      setError('Хамгийн багадаа 3 гишүүний мэдээлэл оруулна уу'); return;
    }

    setLoading(true);
    setError('');

    const fd = new FormData();
    fd.append('org_name', org_name);
    fd.append('registration_number', registration_number);
    fd.append('address', address);
    fd.append('contact_phone', contact_phone);
    fd.append('contact_email', contact_email);
    fd.append('district', district);
    fd.append('khoroo', khoroo);
    fd.append('board_members', JSON.stringify(validMembers));
    // Баримт бичгүүд заавал биш — хавсаргасныг нь л илгээнэ.
    if (meetingRef.current?.files?.[0]) fd.append('meeting_protocol', meetingRef.current.files[0]);
    if (resolutionRef.current?.files?.[0]) fd.append('board_resolution', resolutionRef.current.files[0]);
    if (signaturesRef.current?.files?.[0]) fd.append('signatures_doc', signaturesRef.current.files[0]);
    if (propertyRef.current?.files?.[0]) fd.append('property_doc', propertyRef.current.files[0]);

    const hasFiles = !!(meetingRef.current?.files?.[0] || resolutionRef.current?.files?.[0]
      || signaturesRef.current?.files?.[0] || propertyRef.current?.files?.[0]);
    try {
      const res = await fetch(`${API}/hoa-registration`, { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Алдаа гарлаа');
      }
      const data = await res.json().catch(() => ({}));
      setRequestId(data.request_id || '');
      setSuccess(true);
    } catch (err: any) {
      const base = err.message || 'Алдаа гарлаа';
      setError(`${base} ${SUPPORT_LINE}`);
      // Файл хавсаргасан үед байршуулалт амжилтгүй бол алерт гаргана.
      if (hasFiles) {
        window.alert(`Файл байршуулахад алдаа гарлаа.\n\n${SUPPORT_LINE}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const laterMeetingRef = useRef<HTMLInputElement>(null);
  const laterResolutionRef = useRef<HTMLInputElement>(null);
  const laterSignaturesRef = useRef<HTMLInputElement>(null);
  const laterPropertyRef = useRef<HTMLInputElement>(null);

  const handleAddDocs = async () => {
    const refs = [laterMeetingRef, laterResolutionRef, laterSignaturesRef, laterPropertyRef];
    const names = ['meeting_protocol', 'board_resolution', 'signatures_doc', 'property_doc'];
    const fd = new FormData();
    let any = false;
    refs.forEach((r, i) => {
      const f = r.current?.files?.[0];
      if (f) { fd.append(names[i], f); any = true; }
    });
    if (!any) { setDocMsg('Дор хаяж нэг файл сонгоно уу'); return; }

    setAddingDocs(true);
    setDocMsg('');
    try {
      const res = await fetch(`${API}/hoa-registration/${requestId}/files`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Алдаа гарлаа');
      setDocMsg(`✅ ${data.message}`);
      refs.forEach(r => { if (r.current) r.current.value = ''; });
    } catch (err: any) {
      setDocMsg(`${err.message || 'Алдаа гарлаа'} ${SUPPORT_LINE}`);
      window.alert(`Файл байршуулахад алдаа гарлаа.\n\n${SUPPORT_LINE}`);
    } finally {
      setAddingDocs(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f1e8', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: 16, padding: 40, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{reupload ? '📎' : '✅'}</div>
            {reupload ? (
              <>
                <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22', marginBottom: 12 }}>Нэмэлт баримт хавсаргах</h2>
                <p style={{ color: '#5f6f66', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
                  Дүүргээс таны бүртгэлд нэмэлт баримт шаардсан байна. Доор шаардсан баримтуудаа хавсаргаад илгээнэ үү.
                </p>
              </>
            ) : (
              <>
                <h2 style={{ fontFamily: "'Inter'", fontWeight: 700, color: '#1c2b22', marginBottom: 12 }}>Хүсэлт амжилттай илгээгдлээ</h2>
                <p style={{ color: '#5f6f66', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
                  Таны СӨХ бүртгэлийн хүсэлтийг хүлээн авлаа. Дүүргийн холбогдох албан тушаалтан баримт бичгүүдийг хянаж,
                  бүртгэл баталгаажсан тухай <strong>{contact_email}</strong> хаягаар мэдэгдэл илгээнэ.
                </p>
              </>
            )}
          </div>

          {/* Add documents later */}
          <div style={{ background: '#fbfaf6', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #e8e5dc', textAlign: 'left' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: '#236844', margin: '0 0 6px' }}>📎 Баримт бичиг нэмж хавсаргах</h3>
            <p style={{ fontSize: 13, color: '#5f6f66', margin: '0 0 14px', lineHeight: 1.5 }}>
              Баримт бичгээ одоо эсвэл дараа нь хавсаргаж болно. Хянагдахаас өмнө хэдэн ч удаа нэмэх/солих боломжтой.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              <div><label style={smLabel}>Хурлын протокол</label><input ref={laterMeetingRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" /></div>
              <div><label style={smLabel}>УЗ, ХЗ тогтоол</label><input ref={laterResolutionRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" /></div>
              <div><label style={smLabel}>Гарын үсэг бүхий тогтоол</label><input ref={laterSignaturesRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" /></div>
              <div><label style={smLabel}>Өмчлөлийн баримт</label><input ref={laterPropertyRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" /></div>
            </div>
            {docMsg && (
              <div style={{ marginTop: 12, fontSize: 13, color: docMsg.startsWith('✅') ? '#236844' : '#dc2626' }}>{docMsg}</div>
            )}
            <button onClick={handleAddDocs} disabled={addingDocs} style={{ ...btnOutline, marginTop: 14, padding: '10px 20px', fontSize: 14, opacity: addingDocs ? 0.6 : 1 }}>
              {addingDocs ? 'Хавсаргаж байна...' : 'Баримт хавсаргах'}
            </button>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button onClick={() => navigate('/welcome')} style={{ ...btnPrimary, padding: '12px 32px' }}>Нүүр хуудас руу буцах</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f1e8', padding: '20px 20px 60px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32, paddingTop: 20 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #2f7d52, #236844)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
              <path d="M14 22 L14 41 L23 41 L23 33 L25 33 L25 41 L34 41 L34 22" stroke="#fff" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
              <path d="M9 24 L24 11 L39 24" stroke="#fff" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
              <path d="M24 19c-2.4-2.6-6.6-1.3-6.6 2 0 2.6 3.4 4.7 6.6 7 3.2-2.3 6.6-4.4 6.6-7 0-3.3-4.2-4.6-6.6-2Z" fill="#ffd166" />
            </svg>
          </div>
          <h1 style={{ fontFamily: "'Inter'", fontSize: 24, fontWeight: 700, color: '#1c2b22', margin: 0 }}>СӨХ шинээр бүртгүүлэх</h1>
          <p style={{ color: '#5f6f66', fontSize: 14, marginTop: 8 }}>Доорх маягтыг бөглөж бүртгэлийн хүсэлт илгээнэ үү</p>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Org info */}
          <div style={sectionCard}>
            <h3 style={sectionTitle}>СӨХ мэдээлэл</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>СӨХ нэр *</label>
              <input value={org_name} onChange={e => setOrgName(e.target.value)} style={inputStyle} placeholder="Нарны хороолол СӨХ" required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Регистрийн дугаар</label>
                <input value={registration_number} onChange={e => setRegNumber(e.target.value)} style={inputStyle} placeholder="1234567" />
              </div>
              <div>
                <label style={labelStyle}>Хаяг</label>
                <input value={address} onChange={e => setAddress(e.target.value)} style={inputStyle} placeholder="БЗД, 1-р хороо" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Утас *</label>
                <input value={contact_phone} onChange={e => setPhone(e.target.value)} style={inputStyle} placeholder="7000-1234" required />
              </div>
              <div>
                <label style={labelStyle}>И-мэйл *</label>
                <input value={contact_email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="info@sokh.mn" type="email" required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Дүүрэг</label>
                <select
                  value={district}
                  onChange={e => { setDistrict(e.target.value); setKhoroo(''); }}
                  style={inputStyle}
                >
                  <option value="">— Дүүрэг сонгох —</option>
                  {UB_DISTRICTS.map(d => (
                    <option key={d.name} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Хороо</label>
                <select
                  value={khoroo}
                  onChange={e => setKhoroo(e.target.value)}
                  style={{ ...inputStyle, opacity: district ? 1 : 0.6 }}
                  disabled={!district}
                >
                  <option value="">{district ? '— Хороо сонгох —' : 'Эхлээд дүүрэг сонгоно уу'}</option>
                  {khorooOptions(district).map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Board members */}
          <div style={sectionCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 0 }}>Удирдах зөвлөл, хяналтын зөвлөлийн гишүүд</h3>
              <button type="button" onClick={addMember} style={{ ...btnOutline, padding: '6px 14px', fontSize: 13 }}>+ Гишүүн нэмэх</button>
            </div>
            {members.map((m, i) => (
              <div key={i} style={{ background: '#fbfaf6', borderRadius: 10, padding: 16, marginBottom: 12, border: '1px solid #e8e5dc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#236844' }}>
                    {m.role === 'director' ? 'Захирал (УЗ дарга)' : `Гишүүн ${i}`}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select value={m.role} onChange={e => updateMember(i, 'role', e.target.value)} style={{ ...inputStyle, padding: '4px 8px', fontSize: 12, width: 'auto' }}>
                      <option value="director">Захирал</option>
                      <option value="board">УЗ гишүүн</option>
                      <option value="audit">ХЗ гишүүн</option>
                      <option value="member">Гишүүн</option>
                    </select>
                    {members.length > 3 && (
                      <button type="button" onClick={() => removeMember(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
                  <div>
                    <label style={smLabel}>Овог нэр *</label>
                    <input value={m.name} onChange={e => updateMember(i, 'name', e.target.value)} style={inputStyle} placeholder="Бат Дорж" />
                  </div>
                  <div>
                    <label style={smLabel}>И-мэйл</label>
                    <input value={m.email} onChange={e => updateMember(i, 'email', e.target.value)} style={inputStyle} placeholder="bat@mail.mn" type="email" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={smLabel}>Утас *</label>
                    <input value={m.phone} onChange={e => updateMember(i, 'phone', e.target.value)} style={inputStyle} placeholder="9900-0000" />
                  </div>
                  <div>
                    <label style={smLabel}>Регистр</label>
                    <input value={m.register} onChange={e => updateMember(i, 'register', e.target.value)} style={inputStyle} placeholder="АА00112233" />
                  </div>
                  <div>
                    <label style={smLabel}>Ажлын газар</label>
                    <input value={m.workplace} onChange={e => updateMember(i, 'workplace', e.target.value)} style={inputStyle} placeholder="Компани нэр" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* File uploads */}
          <div style={sectionCard}>
            <h3 style={{ ...sectionTitle, marginBottom: 6 }}>Хавсаргах баримт бичгүүд</h3>
            <p style={{ fontSize: 13, color: '#5f6f66', margin: '0 0 16px', lineHeight: 1.5 }}>
              Баримт бичгүүд <strong>заавал биш</strong>. Хүсэлт илгээсний дараа
              дүүргийн байцаагчийн хүсэлтээр нэмж хавсаргах боломжтой.
            </p>
            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label style={labelStyle}>Нийт оршин суугчдын хурлын протокол</label>
                <input ref={meetingRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" />
              </div>
              <div>
                <label style={labelStyle}>Удирдах зөвлөл, хяналтын зөвлөлийн тогтоол</label>
                <input ref={resolutionRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" />
              </div>
              <div>
                <label style={labelStyle}>Нийт оршин суугчдын дэмжсэн гарын үсэг бүхий тогтоол</label>
                <input ref={signaturesRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" />
              </div>
              <div>
                <label style={labelStyle}>Өмчлөлийг нотлох баримт бичиг</label>
                <input ref={propertyRef} type="file" style={fileInput} accept=".pdf,.doc,.docx,.jpg,.png" />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button type="button" onClick={() => navigate('/welcome')} style={{ ...btnOutline, padding: '12px 24px' }}>Буцах</button>
            <button type="submit" disabled={loading} style={{ ...btnPrimary, flex: 1, padding: '14px 24px', fontSize: 15, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Илгээж байна...' : 'Хүсэлт илгээх'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
  padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const btnOutline: React.CSSProperties = {
  background: 'transparent', color: '#236844', border: '1.5px solid #236844',
  padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const sectionCard: React.CSSProperties = {
  background: '#fff', borderRadius: 14, padding: 24, marginBottom: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const sectionTitle: React.CSSProperties = {
  fontFamily: "'Inter'", fontWeight: 600, fontSize: 16, color: '#1c2b22', marginBottom: 16, margin: '0 0 16px',
};
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 6, fontSize: 13, color: '#2a3b32', fontWeight: 500 };
const smLabel: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 12, color: '#5f6f66', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #dcdacf', borderRadius: 10,
  fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6',
};
const fileInput: React.CSSProperties = {
  width: '100%', padding: '10px 14px', border: '1.5px dashed #c8c5b9', borderRadius: 10,
  fontSize: 13, boxSizing: 'border-box', background: '#fbfaf6', cursor: 'pointer',
};
